import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Track } from '../../api.js';
import AttentionStrip from './AttentionStrip.js';
import BoardCard from './BoardCard.js';
import { distinctModules, moduleOf } from './moduleMap.js';
import { boardColumnFor, isActionable, parkedLaneFor, type BoardColumn, type ParkedLane } from './statusMap.js';
import ThemedShell from './ThemedShell.js';
import TrackDrawer from './TrackDrawer.js';
import { useTracks } from './useTracks.js';

const COLUMNS: { key: BoardColumn; label: string; dotVar: string }[] = [
  { key: 'spec_review', label: 'Spec Review', dotVar: 'var(--warn)' },
  { key: 'queued', label: 'Queued', dotVar: 'var(--faint)' },
  { key: 'building', label: 'Building', dotVar: 'var(--run)' },
  { key: 'in_review', label: 'In Review', dotVar: 'var(--warn)' },
  { key: 'done', label: 'Done', dotVar: 'var(--ok)' },
];

const WARN_COLUMNS: BoardColumn[] = ['spec_review', 'in_review'];

function sortByUpdatedDesc(tracks: Track[]): Track[] {
  return [...tracks].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
}

function ParkedLaneSection({
  lane,
  title,
  dotVar,
  tracks,
  onOpen,
  defaultOpenWhenNonEmpty,
}: {
  lane: ParkedLane;
  title: string;
  dotVar: string;
  tracks: Track[];
  onOpen: (id: string) => void;
  defaultOpenWhenNonEmpty?: boolean;
}) {
  const [manuallySet, setManuallySet] = useState<boolean | null>(null);
  if (tracks.length === 0) return null;

  const open = defaultOpenWhenNonEmpty ? manuallySet ?? true : manuallySet ?? false;

  return (
    <details
      className="board-lane"
      data-lane={lane}
      open={open}
      onToggle={(e) => setManuallySet((e.target as HTMLDetailsElement).open)}
    >
      <summary className="board-lane-summary">
        <span className="board-lane-caret">▸</span>
        <span className="board-lane-dot" style={{ background: dotVar }} />
        <span className="board-lane-title">{title}</span>
        <span className="board-lane-count">{tracks.length}</span>
      </summary>
      <div className="board-lane-cards">
        {sortByUpdatedDesc(tracks).map((track) => (
          <BoardCard key={track.id} track={track} isWarnColumn={false} onOpen={onOpen} />
        ))}
      </div>
    </details>
  );
}

export default function Board() {
  const { tracks, loading, error, refresh } = useTracks();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openTrackId, setOpenTrackId] = useState<string | null>(() => searchParams.get('track'));
  const [activeModule, setActiveModule] = useState<string>(() => searchParams.get('module') ?? 'all');

  function openTrack(id: string) {
    setOpenTrackId(id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('track', id);
      return next;
    });
  }

  function closeTrack() {
    setOpenTrackId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('track');
      return next;
    });
  }

  function selectModule(module: string) {
    setActiveModule(module);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (module === 'all') next.delete('module');
      else next.set('module', module);
      return next;
    });
  }

  const modules = useMemo(() => distinctModules(tracks), [tracks]);
  const filteredTracks = useMemo(
    () => (activeModule === 'all' ? tracks : tracks.filter((t) => moduleOf(t) === activeModule)),
    [tracks, activeModule],
  );

  const columnTracks = useMemo(() => {
    const grouped: Record<BoardColumn, Track[]> = {
      spec_review: [],
      queued: [],
      building: [],
      in_review: [],
      done: [],
    };
    for (const track of filteredTracks) {
      const column = boardColumnFor(track);
      if (column) grouped[column].push(track);
    }
    for (const key of Object.keys(grouped) as BoardColumn[]) {
      grouped[key] = sortByUpdatedDesc(grouped[key]);
    }
    return grouped;
  }, [filteredTracks]);

  const blockedTracks = filteredTracks.filter((t) => parkedLaneFor(t) === 'blocked');
  const cancelledTracks = filteredTracks.filter((t) => parkedLaneFor(t) === 'cancelled');

  const needAction = tracks.filter((t) => isActionable(t.state)).length;

  return (
    <ThemedShell activeNav="board">
      <div className="board-page">
        <div className="board-header">
          <h1 className="board-heading">Pipeline</h1>
          <span className="board-header-counts">
            {needAction} need action / {tracks.length} tracks
          </span>
        </div>

        {loading && <div className="empty">Loading…</div>}
        {error && (
          <div
            style={{
              background: 'var(--bad-bg)',
              color: 'var(--bad)',
              padding: '14px 16px',
              borderRadius: 'var(--radius-sm)',
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <AttentionStrip tracks={tracks} onOpen={openTrack} />

            <div className="board-filter-bar">
              <button
                type="button"
                className={`board-filter-chip${activeModule === 'all' ? ' board-filter-chip-active' : ''}`}
                onClick={() => selectModule('all')}
              >
                All
              </button>
              {modules.map((module) => (
                <button
                  key={module}
                  type="button"
                  className={`board-filter-chip${activeModule === module ? ' board-filter-chip-active' : ''}`}
                  onClick={() => selectModule(module)}
                >
                  {module}
                </button>
              ))}
            </div>

            <div className="board-columns">
              {COLUMNS.map((col) => (
                <div className="board-column" key={col.key}>
                  <div className="board-column-header">
                    <span className="board-column-dot" style={{ background: col.dotVar }} />
                    <span className="board-column-title">{col.label}</span>
                    <span className="board-column-count">{columnTracks[col.key].length}</span>
                  </div>
                  <div className="board-column-cards">
                    {columnTracks[col.key].map((track) => (
                      <BoardCard
                        key={track.id}
                        track={track}
                        isWarnColumn={WARN_COLUMNS.includes(col.key)}
                        onOpen={openTrack}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="board-parked-lanes">
              <ParkedLaneSection
                lane="blocked"
                title="Blocked"
                dotVar="var(--bad)"
                tracks={blockedTracks}
                onOpen={openTrack}
                defaultOpenWhenNonEmpty
              />
              <ParkedLaneSection
                lane="cancelled"
                title="Cancelled"
                dotVar="var(--faint)"
                tracks={cancelledTracks}
                onOpen={openTrack}
              />
            </div>
          </>
        )}

        <TrackDrawer trackId={openTrackId} tracks={tracks} onClose={closeTrack} refreshList={refresh} />
      </div>
    </ThemedShell>
  );
}
